import { drawBrandMark } from "@/lib/canvasBrand";
import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Share2, Loader2, Copy, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { fill } from "@/lib/i18nRich";

const LOCALES = { fr: "fr-FR", en: "en-GB", es: "es-ES", pt: "pt-PT" };
import { downloadPoster, sharePoster } from "@/lib/posterShare";
import { toShareLink } from "@/lib/shareLinks";

const W = 1080, H = 1080;

// Background themes the preacher can pick from.
const THEMES = [
  { key: "foret", name: "Forêt", nameKey: "poster.theme_foret", swatch: "#1c3a2c", stops: [[0, "#1c3a2c"], [0.55, "#0E1A14"], [1, "#070d0a"]] },
  { key: "or", name: "Doré", nameKey: "poster.theme_or", swatch: "#5a4413", stops: [[0, "#5a4413"], [0.5, "#241a06"], [1, "#0c0900"]] },
  { key: "nuit", name: "Nuit", nameKey: "poster.theme_nuit", swatch: "#1e2a4a", stops: [[0, "#1e2a4a"], [0.55, "#0d1226"], [1, "#05070f"]] },
  { key: "royal", name: "Royal", nameKey: "poster.theme_royal", swatch: "#3a1f52", stops: [[0, "#3a1f52"], [0.55, "#1b0f2b"], [1, "#0a0512"]] },
  { key: "aurore", name: "Aurore", nameKey: "poster.theme_aurore", swatch: "#0f3f45", stops: [[0, "#0f3f45"], [0.55, "#0a2126"], [1, "#04100f"]] },
];

const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

const wrapText = (ctx, text, x, y, maxWidth, lineHeight, maxLines) => {
  const words = String(text || "").split(" ");
  let line = "";
  const lines = [];
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) { lines.push(line); line = w; }
    else line = test;
  }
  if (line) lines.push(line);
  const shown = lines.slice(0, maxLines);
  shown.forEach((ln, i) => ctx.fillText(ln, x, y + i * lineHeight));
  return shown.length;
};

const coverDraw = (ctx, img) => {
  const ir = img.width / img.height, cr = W / H;
  let dw = W, dh = H, dx = 0, dy = 0;
  if (ir > cr) { dh = H; dw = H * ir; dx = (W - dw) / 2; }
  else { dw = W; dh = W / ir; dy = (H - dh) / 2; }
  ctx.drawImage(img, dx, dy, dw, dh);
};

// Generates a customisable, branded share poster for a scheduled live.
export const SharePoster = ({ open, onOpenChange, title, dateISO, hostName, shareUrl: rawShareUrl }) => {
  const { t, lang } = useApp();
  const shareUrl = rawShareUrl ? toShareLink(rawShareUrl) : "";
  const canvasRef = useRef(null);
  const [imgUrl, setImgUrl] = useState(null);
  const [building, setBuilding] = useState(true);
  const [themeKey, setThemeKey] = useState("foret");
  const [bgImage, setBgImage] = useState(null); // object URL of an uploaded background

  const fmtDate = () => { try { return new Date(dateISO).toLocaleString(LOCALES[lang] || "fr-FR", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
  const countdownText = () => {
    try {
      const diff = new Date(dateISO).getTime() - Date.now();
      if (diff <= 60000) return t("poster.starts_soon", "Commence bientôt");
      const min = Math.floor(diff / 60000);
      if (min < 60) return fill(t("poster.starts_in_min", "Commence dans {n} min"), { n: min });
      const h = Math.floor(min / 60), m = min % 60;
      if (h < 24) return fill(t("poster.starts_in_h", "Commence dans {h} h{m}"), { h, m: m ? ` ${String(m).padStart(2, "0")}` : "" });
      return fill(t("poster.starts_in_d", "Commence dans {n} j"), { n: Math.floor(h / 24) });
    } catch { return ""; }
  };
  const shareText = fill(t("poster.live_share_text", "🔴 {host} en direct : {title}\n⏳ {countdown}\n📅 {date}\nRejoignez-moi sur Yalola 🙏"), { host: hostName || "", title, countdown: countdownText(), date: fmtDate() });

  useEffect(() => {
    if (!open) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) { setBuilding(false); return undefined; }
    setBuilding(true);
    setImgUrl(null);
    let cancelled = false;

    const render = (bgImg) => {
      if (cancelled) return;
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext("2d");
      if (bgImg) {
        coverDraw(ctx, bgImg);
        const ov = ctx.createLinearGradient(0, 0, W, H);
        ov.addColorStop(0, "rgba(8,16,12,0.45)"); ov.addColorStop(1, "rgba(8,16,12,0.85)");
        ctx.fillStyle = ov; ctx.fillRect(0, 0, W, H);
      } else {
        const th = THEMES.find((t) => t.key === themeKey) || THEMES[0];
        const g = ctx.createLinearGradient(0, 0, W, H);
        th.stops.forEach(([o, c]) => g.addColorStop(o, c));
        ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = "rgba(224,166,60,0.12)";
        ctx.beginPath(); ctx.arc(W * 0.86, H * 0.14, 260, 0, Math.PI * 2); ctx.fill();
        ctx.beginPath(); ctx.arc(W * 0.08, H * 0.92, 220, 0, Math.PI * 2); ctx.fill();
      }

      ctx.fillStyle = "#e11d48";
      roundRect(ctx, 90, 120, 320, 74, 37); ctx.fill();
      ctx.fillStyle = "#fff"; ctx.font = "bold 40px Outfit, Arial, sans-serif"; ctx.textBaseline = "middle"; ctx.textAlign = "left";
      ctx.fillText(t("poster.live_badge", "● EN DIRECT"), 122, 158);

      ctx.textBaseline = "top";
      ctx.fillStyle = "#fff"; ctx.font = "bold 82px Outfit, Arial, sans-serif";
      const n = wrapText(ctx, title, 90, 300, W - 180, 96, 4);
      let cy = 300 + n * 96 + 34;
      ctx.fillStyle = "#E0A63C"; ctx.font = "600 46px Outfit, Arial, sans-serif";
      ctx.fillText(fill(t("poster.live_with", "avec {name}"), { name: hostName || "" }), 90, cy);
      cy += 74;
      ctx.fillStyle = "rgba(255,255,255,0.92)"; ctx.font = "40px Inter, Arial, sans-serif";
      ctx.fillText(fill(t("poster.live_when", "Quand : {date}"), { date: fmtDate() }), 90, cy);
      cy += 62;
      ctx.fillStyle = "#E0A63C"; ctx.font = "bold 46px Outfit, Arial, sans-serif";
      ctx.fillText(countdownText(), 90, cy);

      // Vector Yalola brandmark (no external image → toBlob never taints).
      drawBrandMark(ctx, 150, H - 130, 56);
      ctx.textAlign = "left";
      ctx.fillStyle = "#fff"; ctx.font = "bold 54px Outfit, Arial, sans-serif";
      ctx.fillText("Yalola", 225, H - 148);
      ctx.fillStyle = "rgba(255,255,255,0.7)"; ctx.font = "30px Inter, Arial, sans-serif";
      ctx.fillText(t("poster.brand_tagline", "Le réseau chrétien qui rassemble"), 225, H - 100);

      try {
        canvas.toBlob((b) => { if (!cancelled) { if (b) setImgUrl(URL.createObjectURL(b)); setBuilding(false); } }, "image/png");
      } catch { setBuilding(false); }
    };

    if (bgImage) {
      const im = new Image();
      im.onload = () => render(im);
      im.onerror = () => render(null);
      im.src = bgImage;
    } else {
      render(null);
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, title, dateISO, hostName, themeKey, bgImage]);

  const onPickImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (bgImage) URL.revokeObjectURL(bgImage);
    setBgImage(URL.createObjectURL(f));
  };

  const download = () => downloadPoster({ url: imgUrl, fileName: "yalola-direct.png", t });
  const copyLink = async () => { try { await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`); toast.success(t("poster.copied_text", "Texte copié — collez-le sur WhatsApp ou Facebook")); } catch { toast.error(t("poster.copy_fail", "Copie impossible")); } };
  const doNativeShare = () => sharePoster({ url: imgUrl, text: shareText, link: shareUrl, fileName: "yalola-direct.png", copiedMessage: t("poster.copied_text", "Texte copié — collez-le sur WhatsApp ou Facebook"), t });
  const share = () => doNativeShare();

  return (
    <>
      <canvas ref={canvasRef} style={{ display: "none" }} aria-hidden="true" />
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-sm" data-testid="share-poster-modal">
          <DialogHeader>
            <DialogTitle>{t("poster.live_title", "Partager le direct")}</DialogTitle>
            <DialogDescription>{t("poster.customize_hint", "Personnalisez l'affiche, puis partagez-la sur WhatsApp, Facebook…")}</DialogDescription>
          </DialogHeader>

          <div className="rounded-xl overflow-hidden border" style={{ borderColor: "var(--yalola-border)", aspectRatio: "1 / 1" }}>
            {building && <div className="w-full h-full grid place-items-center"><Loader2 className="animate-spin" style={{ color: "var(--yalola-primary)" }} /></div>}
            {!building && imgUrl && <img src={imgUrl} alt={t("poster.live_alt", "Affiche du direct")} className="w-full block" data-testid="share-poster-image" />}
          </div>

          {/* Background chooser */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="share-poster-themes">
            {THEMES.map((th) => (
              <button
                key={th.key}
                onClick={() => { setBgImage(null); setThemeKey(th.key); }}
                title={t(th.nameKey, th.name)}
                className="h-8 w-8 rounded-full border-2 transition-transform hover:scale-110"
                style={{ background: th.swatch, borderColor: !bgImage && themeKey === th.key ? "var(--yalola-primary)" : "transparent" }}
                data-testid={`share-poster-theme-${th.key}`}
              />
            ))}
            <label className="h-8 px-2.5 inline-flex items-center gap-1 rounded-full border text-xs font-semibold cursor-pointer" style={{ borderColor: bgImage ? "var(--yalola-primary)" : "var(--yalola-border)", color: "var(--yalola-primary)" }} data-testid="share-poster-image-upload">
              <ImagePlus size={14} /> {t("poster.image", "Image")}
              <input type="file" accept="image/*" className="hidden" onChange={onPickImage} />
            </label>
            {bgImage && (
              <button onClick={() => setBgImage(null)} className="h-8 w-8 grid place-items-center rounded-full text-red-500" title={t("poster.remove_image", "Retirer l'image")} data-testid="share-poster-image-clear"><X size={15} /></button>
            )}
          </div>

          <div className="flex gap-2">
            <Button onClick={share} disabled={building} className="flex-1 pill-primary rounded-full" data-testid="share-poster-share"><Share2 size={16} className="mr-1.5" /> {t("poster.share", "Partager")}</Button>
            <Button onClick={download} disabled={building} variant="outline" className="rounded-full" title={t("poster.download", "Télécharger")} data-testid="share-poster-download"><Download size={16} /></Button>
            <Button onClick={copyLink} variant="outline" className="rounded-full" title={t("poster.copy_link", "Copier le lien")} data-testid="share-poster-copy"><Copy size={16} /></Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SharePoster;
